/**
 * ONE PACKAGE, ONE STORY.
 *
 * OMNΛS already knows everything on this panel. It knows it in six different
 * places — the requirements register, the matching engine, the shortlist, the
 * RFQ release table, the award workflow, and the realism engine — and a PM
 * who wants to understand one package has, until now, had to visit all six.
 *
 * This is the join, not new knowledge. Every facet reports an answer some
 * backend already gave and links to the workspace that owns it. Nothing here
 * decides eligibility, readiness, commercial standing or execution impact,
 * and a facet with no backend answer says "not read" rather than defaulting
 * to a reassuring one.
 *
 * The three judgements stay apart here as everywhere else: eligibility,
 * operational fit and relationship are three separate rows and are never
 * summed, ranked together, or reduced to one figure.
 */
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { UnknownValue } from "../../components/workflow/WorkflowStates";
import { presentPackageStory } from "./exchangePresentation";
import { ArrowUpRight, BookOpen } from "lucide-react";

const TONE_CLASS = {
  good: "text-emerald-400",
  active: "text-blue-400",
  attention: "text-amber-400",
  unknown: "text-muted-foreground",
};

/** Where each facet's canonical workspace lives. */
const LINK_LABEL = {
  requirements: "Requirements",
  package: "Bid package",
  readiness: "What can start",
};

const PackageStoryPanel = ({
  row,
  sourcing,
  shortlist,
  releases,
  requirements,
  requirementsTruncated,
  awardProposals,
  realism,
  onOpen,
}) => {
  const facets = presentPackageStory({
    row,
    sourcing,
    shortlist,
    releases,
    requirements,
    requirementsTruncated,
    awardProposals,
    realism,
  });

  return (
    <Card className="border-border">
      <CardContent className="py-3 px-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-semibold flex items-center gap-1.5">
            <BookOpen className="w-4 h-4 text-primary" />
            This package, end to end
          </p>
          <p className="text-[11px] text-muted-foreground">
            Every line is a backend answer. Open the workspace that owns it to act.
          </p>
        </div>

        {/*
          This panel lives inside the assembly column, not the viewport, so
          viewport breakpoints would size these cells against a width they
          never have — on a normal laptop a six-column grid here collapses to
          about seventy pixels a cell. An auto-fill track sizes to the space
          the panel actually got, at any container width.
        */}
        <div className="grid gap-x-4 gap-y-3 [grid-template-columns:repeat(auto-fill,minmax(9rem,1fr))]">
          {facets.map((facet) => (
            <div key={facet.label} className="min-w-0 space-y-0.5">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                {facet.label}
              </p>
              {facet.value === null ? (
                <UnknownValue reason={facet.detail || undefined} />
              ) : (
                /* A verdict that has been cut in half is worse than a small
                   one, so these wrap rather than truncate. */
                <p className={`text-sm font-semibold break-words ${TONE_CLASS[facet.tone]}`}>
                  {facet.value}
                </p>
              )}
              {facet.detail && facet.value !== null && (
                <p className="text-[10px] text-muted-foreground">{facet.detail}</p>
              )}
              {facet.link && onOpen && (
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-[10px] text-muted-foreground hover:text-foreground"
                  onClick={() => onOpen(facet.link)}
                >
                  {LINK_LABEL[facet.link] || "Open"}
                  <ArrowUpRight className="w-2.5 h-2.5 ml-0.5" />
                </Button>
              )}
            </div>
          ))}
        </div>

        {/* The three judgements are three rows above, and stay three rows. */}
        <p className="text-[10px] text-muted-foreground border-t border-border/50 pt-2">
          Eligibility, operational fit and relationship are separate judgements. A provider that
          fails a mandatory requirement is never made attractive by the other two, and OMNΛS
          computes no overall score.
        </p>
      </CardContent>
    </Card>
  );
};

export default PackageStoryPanel;
